import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Facebook, Instagram, Linkedin, Twitter, Edit, Trash2, Clock, CheckCircle, XCircle, Calendar, Plus, Menu, Copy, Ban, ChevronDown, ChevronUp, AlertTriangle, Send, Eye, Loader2, ExternalLink, User, Building2 } from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import { MobileNav } from '../components/MobileNav';
import { ListPageSkeleton } from '../components/skeletons/ListPageSkeleton';
import { ScheduleSocialPostModal } from '../components/ScheduleSocialPostModal';
import { ConfirmDeleteSocialPostModal } from '../components/ConfirmDeleteSocialPostModal';
import { SocialPostPreviewModal } from '../components/SocialPostPreviewModal';
import { PageHeader } from '../components/design-system/PageHeader';
import { MaintenanceBanner } from '../components/MaintenanceBanner';
import { LowCreditsBanner } from '../components/LowCreditsBanner';
import { useAuth } from '../contexts/AuthContext';
import { useCreditsCheck } from '../hooks/useCreditsCheck';
import { useAIGenerationEnabled } from '../hooks/useAIGenerationEnabled';
import { getSocialPostTimeStatus } from '../lib/socialPostStatusHelper';
import { loadScheduleTargets, NO_TARGETS, type ScheduleTargets } from '../lib/scheduleTargets';
import {
  unconnectedScheduledPlatforms,
  type ConnectedPlatformsMap,
} from '../lib/socialConnections';
import { parseImages } from '../lib/socialImages';
import { removeSocialImageFile } from '../lib/socialImageStorage';

interface SocialPost {
  id: string;
  platform: string;
  post_content: string;
  status: string;
  scheduled_at: string | null;
  published_at: string | null;
  created_at: string;
  error_message: string | null;
  blog_id: string | null;
  image_url: string | null;
  facebook_page_id: string | null;
  linkedin_page_id: string | null;
  up_status: string | null;
  retry_count: number | null;
  post_url: string | null;
}

export default function SocialMediaPosts() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  const creditsInfo = useCreditsCheck(user?.id);
  const { isEnabled: aiGenerationEnabled } = useAIGenerationEnabled();
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('draft');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<SocialPost | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewPost, setPreviewPost] = useState<SocialPost | null>(null);
  const [unconnectedPlatforms, setUnconnectedPlatforms] = useState<string[]>([]);
  // Kanaalnamen (LinkedIn-bedrijfspagina's, Facebook-pagina's) per page-id,
  // zodat de card toont naar welk exact kanaal een post is verzonden.
  const [pageNames, setPageNames] = useState<Record<string, string>>({});
  const [, setRefreshTick] = useState(0);
  const [scheduleCheck, setScheduleCheck] = useState<{
    checking: boolean;
    blockReason: string | null;
    targets: ScheduleTargets | null;
  }>({ checking: false, blockReason: null, targets: null });
  const scheduleCheckSeq = useRef(0);

  useEffect(() => {
    loadPosts();
  }, []);

  // Zolang er geplande posts zijn: elke 30 sec de tijdsbadges verversen, en
  // zodra een geplande tijd verstreken is ook de data herladen zodat de
  // statusovergang (gepubliceerd/mislukt) zonder handmatige refresh zichtbaar wordt.
  useEffect(() => {
    if (!posts.some((p) => p.status === 'scheduled')) return;

    const interval = setInterval(() => {
      const hasDuePosts = posts.some(
        (p) =>
          p.status === 'scheduled' &&
          p.scheduled_at &&
          new Date(p.scheduled_at).getTime() <= Date.now()
      );
      if (hasDuePosts) {
        loadPosts();
      } else {
        setRefreshTick((t) => t + 1);
      }
    }, 30_000);

    return () => clearInterval(interval);
  }, [posts]);

  async function loadPosts() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: dbUser } = await supabase
        .from('users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!dbUser) throw new Error('User not found');

      const [{ data, error }, profileResult] = await Promise.all([
        supabase
          .from('social_media_posts')
          .select('*')
          .eq('user_id', dbUser.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('social_profiles')
          .select('connected_platforms')
          .eq('user_id', dbUser.id)
          .maybeSingle(),
      ]);

      if (error) throw error;
      const allPosts = data || [];
      setPosts(allPosts);

      // Banner: alleen ingeplande posts voor een niet-gekoppeld kanaal —
      // concepten worden nooit automatisch geplaatst en tellen niet mee.
      const connected = (profileResult.data?.connected_platforms ??
        {}) as ConnectedPlatformsMap;
      setUnconnectedPlatforms(unconnectedScheduledPlatforms(allPosts, connected));

      // Namen van bedrijfspagina's ophalen zodat de card het exacte kanaal toont.
      // Alleen nodig zodra er posts naar een LinkedIn-bedrijfspagina of
      // Facebook-pagina zijn gegaan; persoonlijke LinkedIn-posts hebben geen naam.
      const needLinkedin = allPosts.some(
        (p) => p.platform === 'linkedin' && p.linkedin_page_id && p.linkedin_page_id !== 'personal',
      );
      const needFacebook = allPosts.some(
        (p) => p.platform === 'facebook' && p.facebook_page_id,
      );
      if (needLinkedin || needFacebook) {
        const [li, fb] = await Promise.all([
          needLinkedin ? loadScheduleTargets('linkedin') : Promise.resolve(NO_TARGETS),
          needFacebook ? loadScheduleTargets('facebook') : Promise.resolve(NO_TARGETS),
        ]);
        const names: Record<string, string> = {};
        for (const pg of [...li.pages, ...fb.pages]) names[pg.id] = pg.name;
        setPageNames(names);
      }
    } catch (err: any) {
      console.error('Error loading posts:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleDeleteClick(post: SocialPost) {
    setPostToDelete(post);
    setDeleteModalOpen(true);
  }

  async function handleDeleteConfirm() {
    if (!postToDelete) return;

    try {
      // Eerst de lijst met bestanden ophalen: na het verwijderen van de rij
      // weten we niet meer welke bestanden erbij hoorden. Pas als de rij ook
      // echt weg is ruimen we de bestanden op, net als in het bewerkscherm:
      // mislukt de rij-verwijdering, dan blijven de bestanden gewoon staan
      // in plaats van dat de post naar weggegooide bestanden wijst.
      const { data: teVerwijderen } = await supabase
        .from('social_media_posts')
        .select('user_id, images')
        .eq('id', postToDelete.id)
        .single();

      const { error } = await supabase
        .from('social_media_posts')
        .delete()
        .eq('id', postToDelete.id);

      if (error) throw error;

      if (teVerwijderen) {
        for (const image of parseImages(teVerwijderen.images)) {
          await removeSocialImageFile(image, postToDelete.id, teVerwijderen.user_id);
        }
      }

      setPosts(posts.filter(p => p.id !== postToDelete.id));
      setDeleteModalOpen(false);
      setPostToDelete(null);
    } catch (err: any) {
      console.error('Error deleting post:', err);
      alert('Fout bij verwijderen: ' + err.message);
    }
  }

  async function handleScheduleClick(post: SocialPost) {
    const seq = ++scheduleCheckSeq.current;
    setSelectedPost(post);
    setScheduleModalOpen(true);

    if (post.platform !== 'facebook' && post.platform !== 'linkedin') {
      setScheduleCheck({ checking: false, blockReason: null, targets: null });
      return;
    }

    setScheduleCheck({ checking: true, blockReason: null, targets: null });
    const targets = await loadScheduleTargets(post.platform);
    if (scheduleCheckSeq.current === seq) {
      setScheduleCheck({
        checking: false,
        blockReason: targets.blockReason,
        targets,
      });
    }
  }

  async function handleScheduleConfirm(scheduledAt: Date, targetPageId?: string | null) {
    if (!selectedPost) return;

    setActionLoading(selectedPost.id);
    try {
      // Gekozen doel op de post snapshotten. LinkedIn persoonlijk = sentinel
      // 'personal' (NULL zou terugvallen op de profiel-default).
      const targetUpdate: Record<string, unknown> = {};
      if (targetPageId !== undefined) {
        if (selectedPost.platform === 'facebook' && targetPageId) {
          targetUpdate.facebook_page_id = targetPageId;
        }
        if (selectedPost.platform === 'linkedin') {
          targetUpdate.linkedin_page_id = targetPageId ?? 'personal';
        }
      }

      const { error } = await supabase
        .from('social_media_posts')
        // Verse start: oude foutmelding weg en de retry-teller terug naar 0,
        // anders is de eerstvolgende mislukte poging meteen definitief.
        .update({
          scheduled_at: scheduledAt.toISOString(),
          status: 'scheduled',
          error_message: null,
          retry_count: 0,
          ...targetUpdate,
        })
        .eq('id', selectedPost.id);

      if (error) throw error;

      await loadPosts();
    } catch (err: any) {
      console.error('Error scheduling post:', err);
      alert('Fout bij inplannen: ' + err.message);
    } finally {
      setActionLoading(null);
      setSelectedPost(null);
      setScheduleCheck({ checking: false, blockReason: null, targets: null });
    }
  }

  async function handleCancelSchedule(post: SocialPost) {
    if (!confirm('Planning annuleren en terug zetten naar concept?')) return;

    setActionLoading(post.id);
    try {
      const { error } = await supabase
        .from('social_media_posts')
        .update({
          scheduled_at: null,
          status: 'draft',
        })
        .eq('id', post.id);

      if (error) throw error;

      await loadPosts();
    } catch (err: any) {
      console.error('Error canceling schedule:', err);
      alert('Fout bij annuleren: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDuplicate(post: SocialPost) {
    setActionLoading(post.id);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: dbUser } = await supabase
        .from('users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!dbUser) throw new Error('User not found');

      const { data, error } = await supabase
        .from('social_media_posts')
        .insert({
          user_id: dbUser.id,
          blog_id: post.blog_id,
          platform: post.platform,
          post_content: post.post_content,
          image_url: post.image_url,
          status: 'draft',
        })
        .select()
        .single();

      if (error) throw error;

      navigate(`/social-media/edit/${data.id}`);
    } catch (err: any) {
      console.error('Error duplicating post:', err);
      alert('Fout bij dupliceren: ' + err.message);
      setActionLoading(null);
    }
  }

  function getPlatformIcon(platform: string) {
    switch (platform) {
      case 'linkedin': return Linkedin;
      case 'facebook': return Facebook;
      case 'instagram': return Instagram;
      case 'x': return Twitter;
      default: return Twitter;
    }
  }

  function getStatusBadge(post: SocialPost) {
    switch (post.status) {
      case 'draft':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <Edit className="w-3 h-3 mr-1" />
          Concept
        </span>;
      case 'scheduled': {
        const timeStatus = getSocialPostTimeStatus(post.scheduled_at, post.status, post.up_status, post.retry_count);
        if (timeStatus.processingStatus === 'processing') {
          return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            {timeStatus.statusLabel}
          </span>;
        }
        if (timeStatus.processingStatus === 'delayed') {
          return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-300">
            <Clock className="w-3 h-3 mr-1" />
            {timeStatus.statusLabel}
          </span>;
        }
        if (timeStatus.processingStatus === 'overdue') {
          return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
            <AlertTriangle className="w-3 h-3 mr-1" />
            {timeStatus.statusLabel}
          </span>;
        }
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Clock className="w-3 h-3 mr-1" />
          Gepland
        </span>;
      }
      case 'published':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Gepubliceerd
        </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          Mislukt
        </span>;
      default:
        return null;
    }
  }

  const filteredPosts = (filterStatus === 'all'
    ? posts
    : posts.filter(p => p.status === filterStatus)).slice();

  // Verzonden posts op werkelijk verzendmoment sorteren (nieuwste eerst).
  // De basisquery sorteert op created_at; voor deze tab is published_at leidend.
  if (filterStatus === 'published') {
    filteredPosts.sort((a, b) => {
      const ta = a.published_at ? new Date(a.published_at).getTime() : 0;
      const tb = b.published_at ? new Date(b.published_at).getTime() : 0;
      return tb - ta;
    });
  }

  const draftCount = posts.filter(p => p.status === 'draft').length;
  const scheduledCount = posts.filter(p => p.status === 'scheduled').length;
  const publishedCount = posts.filter(p => p.status === 'published').length;
  const failedCount = posts.filter(p => p.status === 'failed').length;

  function getPlatformColor(platform: string) {
    switch (platform) {
      case 'linkedin': return '#0A66C2';
      case 'facebook': return '#1877F2';
      case 'instagram': return '#E4405F';
      case 'x': return '#000000';
      default: return '#6B7280';
    }
  }

  function getPlatformGradient(platform: string) {
    switch (platform) {
      case 'linkedin': return 'from-[#0A66C2] to-[#004182]';
      case 'facebook': return 'from-[#1877F2] to-[#0C5BBD]';
      case 'instagram': return 'from-[#E4405F] via-[#F77737] to-[#FCAF45]';
      case 'x': return 'from-gray-800 to-gray-900';
      default: return 'from-gray-400 to-gray-600';
    }
  }

  // Het specifieke kanaal binnen een platform: LinkedIn persoonlijk profiel vs.
  // bedrijfspagina, of de Facebook-pagina. Toont waar mogelijk de paginanaam.
  function getChannelTarget(post: SocialPost): { icon: typeof User; label: string } | null {
    if (post.platform === 'linkedin') {
      if (post.linkedin_page_id === 'personal') {
        return { icon: User, label: 'Persoonlijk profiel' };
      }
      if (post.linkedin_page_id) {
        return { icon: Building2, label: pageNames[post.linkedin_page_id] ?? 'Bedrijfspagina' };
      }
      return null;
    }
    if (post.platform === 'facebook' && post.facebook_page_id) {
      return { icon: Building2, label: pageNames[post.facebook_page_id] ?? 'Bedrijfspagina' };
    }
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <Sidebar onSignOut={handleSignOut} />
        <MobileNav isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} onSignOut={handleSignOut} />
        <div className="lg:pl-64">
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 lg:pt-8">
            <ListPageSkeleton rows={6} withTabs={false} />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <Sidebar onSignOut={handleSignOut} />
      <MobileNav isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} onSignOut={handleSignOut} />

      <div className="lg:pl-64">
        <div className="sticky top-0 z-30 bg-white border-b border-gray-200 lg:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <h1 className="text-lg font-semibold text-[#231F20]">Social media posts</h1>
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-6 h-6 text-[#231F20]" />
            </button>
          </div>
        </div>

        <MaintenanceBanner />
        <LowCreditsBanner />

        <div className="p-6 lg:p-8">
          <div className="max-w-7xl mx-auto space-y-6">
            <PageHeader
              title="Social media posts"
              subtitle="Beheer je social media posts"
              actions={
                <button
                  onClick={() => navigate('/social-media/create')}
                  disabled={!creditsInfo.hasCredits || !aiGenerationEnabled}
                  title={!aiGenerationEnabled ? 'AI generatie is tijdelijk uitgeschakeld' : !creditsInfo.hasCredits ? `Onvoldoende credits. Je hebt ${creditsInfo.total} credits, minimum is ${creditsInfo.negativeLimit}.` : ''}
                  className={`btn-primary inline-flex items-center gap-2 ${(!creditsInfo.hasCredits || !aiGenerationEnabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Plus className="w-5 h-5" />
                  <span className="hidden sm:inline">Nieuwe Post</span>
                </button>
              }
            />

            {unconnectedPlatforms.length > 0 && (
              <div className="card bg-amber-50 border-amber-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-amber-900">
                      {unconnectedPlatforms.length === 1
                        ? `${unconnectedPlatforms[0].charAt(0).toUpperCase()}${unconnectedPlatforms[0].slice(1)} is niet gekoppeld`
                        : 'Een paar kanalen zijn niet gekoppeld'}
                    </p>
                    <p className="text-sm text-amber-800 mt-1">
                      Je hebt posts klaarstaan voor{' '}
                      {unconnectedPlatforms
                        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
                        .join(', ')}
                      , maar die verbinding ontbreekt. Zonder koppeling kan dibbi
                      deze posts niet plaatsen.
                    </p>
                    <button
                      onClick={() => navigate('/dashboard/settings?tab=socials')}
                      className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
                    >
                      Koppel je socials
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!creditsInfo.hasCredits && (
              <div className="card bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-red-900 mb-1">
                      Onvoldoende credits om social media posts te maken
                    </p>
                    <p className="text-sm text-red-800 mb-3">
                      Je hebt {creditsInfo.total} credits. Je kunt geen nieuwe posts maken tot je credits zijn bijgevuld.
                      {creditsInfo.nextBillingDate && (
                        <span className="block mt-1">
                          Volgende automatische bijvulling: <span className="font-semibold">{new Date(creditsInfo.nextBillingDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                        </span>
                      )}
                    </p>
                    <a
                      href="/checkout"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Credits Aankopen
                    </a>
                  </div>
                </div>
              </div>
            )}

        <div className="card p-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'all'
                  ? 'bg-[#231F20] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Alle Posts
            </button>
            <button
              onClick={() => setFilterStatus('draft')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'draft'
                  ? 'bg-[#231F20] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Concept
            </button>
            <button
              onClick={() => setFilterStatus('scheduled')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors inline-flex items-center gap-2 ${
                filterStatus === 'scheduled'
                  ? 'bg-[#231F20] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Gepland
              {scheduledCount > 0 && (
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  filterStatus === 'scheduled'
                    ? 'bg-white/20 text-white'
                    : 'bg-blue-500 text-white'
                }`}>
                  {scheduledCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setFilterStatus('published')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'published'
                  ? 'bg-[#231F20] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Verzonden
            </button>
            {failedCount > 0 && (
              <button
                onClick={() => setFilterStatus('failed')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors inline-flex items-center gap-2 ${
                  filterStatus === 'failed'
                    ? 'bg-[#231F20] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Mislukt
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  filterStatus === 'failed'
                    ? 'bg-white/20 text-white'
                    : 'bg-red-500 text-white'
                }`}>
                  {failedCount}
                </span>
              </button>
            )}
          </div>
        </div>

        {filteredPosts.length === 0 ? (
          <div className="card text-center py-12">
            {posts.length === 0 ? (
              <>
                <p className="text-xl text-gray-600 mb-4">Nog geen social media posts</p>
                <p className="text-gray-500 mb-6">Maak je eerste social media post vanuit je blogs</p>
              </>
            ) : (
              <>
                <p className="text-xl text-gray-600 mb-4">
                  {filterStatus === 'draft'
                    ? 'Geen concepten'
                    : filterStatus === 'scheduled'
                      ? 'Geen geplande posts'
                      : filterStatus === 'published'
                        ? 'Nog niets verzonden'
                        : filterStatus === 'failed'
                          ? 'Geen mislukte posts'
                          : 'Geen posts'}
                </p>
                <p className="text-gray-500 mb-6">
                  {filterStatus === 'draft'
                    ? 'Je hebt geen posts in concept staan.'
                    : filterStatus === 'scheduled'
                      ? 'Je hebt op dit moment niks ingepland.'
                      : filterStatus === 'published'
                        ? 'Hier verschijnen je gepubliceerde posts.'
                        : filterStatus === 'failed'
                          ? 'Mooi — er ging niks mis.'
                          : 'Bekijk een ander tabblad of maak een nieuwe post.'}
                </p>
              </>
            )}
            <button
              onClick={() => navigate('/social-media/create')}
              disabled={!creditsInfo.hasCredits || !aiGenerationEnabled}
              title={!aiGenerationEnabled ? 'AI generatie is tijdelijk uitgeschakeld' : !creditsInfo.hasCredits ? `Onvoldoende credits. Je hebt ${creditsInfo.total} credits, minimum is ${creditsInfo.negativeLimit}.` : ''}
              className={`btn-primary inline-flex items-center gap-2 ${(!creditsInfo.hasCredits || !aiGenerationEnabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Plus className="w-5 h-5" />
              {posts.length === 0 ? 'Maak je eerste post' : 'Nieuwe post'}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredPosts.map((post) => {
              const PlatformIcon = getPlatformIcon(post.platform);
              const platformColor = getPlatformColor(post.platform);
              const platformGradient = getPlatformGradient(post.platform);
              const channel = getChannelTarget(post);
              const ChannelIcon = channel?.icon;
              const previewLength = 100;
              const displayContent = post.post_content.length > previewLength
                ? post.post_content.substring(0, previewLength) + '...'
                : post.post_content;

              return (
                <div
                  key={post.id}
                  className="group card overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer bg-white flex flex-col"
                  onClick={() => {
                    if (post.status === 'draft' || post.status === 'failed') {
                      navigate(`/social-media/edit/${post.id}`);
                    }
                  }}
                >
                  {/* Featured Image or Placeholder */}
                  <div className="relative w-full aspect-[4/3] bg-gray-100 overflow-hidden">
                    {post.image_url ? (
                      <img
                        src={post.image_url}
                        alt="Post afbeelding"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className={`w-full h-full bg-gradient-to-br ${platformGradient} flex items-center justify-center`}>
                        <PlatformIcon className="w-16 h-16 text-white opacity-40" />
                      </div>
                    )}

                    {/* Platform Icon Overlay */}
                    <div
                      className="absolute top-3 left-3 p-2 bg-white rounded-lg shadow-md"
                      style={{ backgroundColor: 'white' }}
                    >
                      <PlatformIcon className="w-5 h-5" style={{ color: platformColor }} />
                    </div>

                    {/* Action Buttons */}
                    <div className="absolute top-3 right-3 flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewPost(post);
                          setPreviewModalOpen(true);
                        }}
                        className="p-2 bg-white rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-blue-50"
                        title="Bekijk preview"
                      >
                        <Eye className="w-4 h-4 text-gray-600 hover:text-blue-600" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(post);
                        }}
                        className="p-2 bg-white rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                        title="Verwijderen"
                      >
                        <Trash2 className="w-4 h-4 text-gray-600 hover:text-red-600" />
                      </button>
                    </div>

                    {/* Status Badge Overlay */}
                    <div className="absolute bottom-3 right-3">
                      {getStatusBadge(post)}
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="flex-1 flex flex-col p-4">
                    {/* Post Preview Text */}
                    <p className="text-sm text-gray-700 leading-relaxed mb-3 line-clamp-3 flex-1">
                      {displayContent}
                    </p>

                    {/* Metadata */}
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-3 pb-3 border-b border-gray-100">
                      {post.status === 'scheduled' && post.scheduled_at && (
                        <>
                          <Clock className="w-3.5 h-3.5" />
                          <span>
                            {new Date(post.scheduled_at).toLocaleString('nl-NL', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </>
                      )}
                      {post.status === 'published' && post.published_at && (
                        <>
                          <Send className="w-3.5 h-3.5" />
                          <span>
                            {new Date(post.published_at).toLocaleString('nl-NL', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </>
                      )}
                      {(post.status === 'draft' || post.status === 'failed') && (
                        <span className="text-gray-400 italic">
                          Nog niet gepland
                        </span>
                      )}
                    </div>

                    {/* Error Message */}
                    {post.status === 'failed' && post.error_message && (
                      <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs text-red-800 line-clamp-2">{post.error_message}</p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2">
                      {(post.status === 'draft' || post.status === 'failed') && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/social-media/edit/${post.id}`);
                            }}
                            className="flex-1 btn-primary inline-flex items-center justify-center gap-2 text-xs py-2"
                            disabled={actionLoading === post.id}
                          >
                            <Edit className="w-3.5 h-3.5" />
                            Bewerken
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleScheduleClick(post);
                            }}
                            className="flex-1 btn-secondary inline-flex items-center justify-center gap-2 text-xs py-2"
                            disabled={actionLoading === post.id}
                          >
                            <Clock className="w-3.5 h-3.5" />
                            Inplannen
                          </button>
                        </>
                      )}
                      {post.status === 'scheduled' && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelSchedule(post);
                            }}
                            className="flex-1 inline-flex items-center justify-center gap-2 text-xs px-3 py-2 border-2 border-amber-500 text-amber-700 bg-amber-50 rounded-lg font-medium hover:bg-amber-100 transition-colors disabled:opacity-50"
                            disabled={actionLoading === post.id}
                          >
                            <Ban className="w-3.5 h-3.5" />
                            {actionLoading === post.id ? 'Bezig...' : 'Annuleren'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/social-media/edit/${post.id}`);
                            }}
                            className="flex-1 btn-secondary inline-flex items-center justify-center gap-2 text-xs py-2"
                          >
                            <Edit className="w-3.5 h-3.5" />
                            Bewerken
                          </button>
                        </>
                      )}
                      {post.status === 'published' && (
                        <>
                          {post.post_url && (
                            <a
                              href={post.post_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 btn-primary inline-flex items-center justify-center gap-2 text-xs py-2"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              Bekijk post
                            </a>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicate(post);
                            }}
                            className="flex-1 btn-secondary inline-flex items-center justify-center gap-2 text-xs py-2"
                            disabled={actionLoading === post.id}
                          >
                            <Copy className="w-3.5 h-3.5" />
                            {actionLoading === post.id ? 'Bezig...' : 'Dupliceer'}
                          </button>
                        </>
                      )}
                      {(post.status === 'draft' || post.status === 'failed') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicate(post);
                          }}
                          className="btn-secondary inline-flex items-center justify-center gap-2 text-xs py-2 px-3"
                          disabled={actionLoading === post.id}
                          title="Dupliceer post"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {/* Platform Name Badge */}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: platformColor }}
                        >
                          <PlatformIcon className="w-3.5 h-3.5" />
                          {post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}
                        </span>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {post.post_content.length} tekens
                        </span>
                      </div>
                      {channel && ChannelIcon && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500 min-w-0">
                          <ChannelIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: platformColor }} />
                          <span className="truncate">{channel.label}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
          </div>
        </div>
      </div>
      {selectedPost && (
        <ScheduleSocialPostModal
          isOpen={scheduleModalOpen}
          onClose={() => {
            setScheduleModalOpen(false);
            setSelectedPost(null);
            setScheduleCheck({ checking: false, blockReason: null, targets: null });
          }}
          onConfirm={handleScheduleConfirm}
          platform={selectedPost.platform}
          checking={scheduleCheck.checking}
          blockReason={scheduleCheck.blockReason}
          targets={scheduleCheck.targets}
          postTargetId={
            selectedPost.platform === 'facebook'
              ? selectedPost.facebook_page_id
              : selectedPost.platform === 'linkedin'
                ? selectedPost.linkedin_page_id
                : null
          }
          onGoToSettings={() => {
            setScheduleModalOpen(false);
            setSelectedPost(null);
            setScheduleCheck({ checking: false, blockReason: null, targets: null });
            navigate('/dashboard/settings?tab=socials');
          }}
        />
      )}

      {postToDelete && (
        <ConfirmDeleteSocialPostModal
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setPostToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          platform={postToDelete.platform}
        />
      )}

      {previewPost && (
        <SocialPostPreviewModal
          isOpen={previewModalOpen}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewPost(null);
          }}
          post={previewPost}
          platformIcon={getPlatformIcon(previewPost.platform)}
          platformColor={getPlatformColor(previewPost.platform)}
          platformGradient={getPlatformGradient(previewPost.platform)}
          onEdit={() => {
            navigate(`/social-media/edit/${previewPost.id}`);
            setPreviewModalOpen(false);
            setPreviewPost(null);
          }}
          onDelete={() => {
            setPreviewModalOpen(false);
            handleDeleteClick(previewPost);
          }}
          onSchedule={() => {
            setPreviewModalOpen(false);
            handleScheduleClick(previewPost);
          }}
          onCancelSchedule={async () => {
            setPreviewModalOpen(false);
            await handleCancelSchedule(previewPost);
            setPreviewPost(null);
          }}
          onDuplicate={async () => {
            setPreviewModalOpen(false);
            await handleDuplicate(previewPost);
            setPreviewPost(null);
          }}
          actionLoading={actionLoading === previewPost.id}
        />
      )}
    </div>
  );
}
